import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import config from '../../config.json';
import TimeEntryTypes from './EntryTypes'

const apiUrl = config.apiUrl;

const TimeEntryUpdate = () => {
    const [timeEntryData, setTimeEntryData] = useState({
        TransactionDate: '',
        EmployeeID: '',
        StartDate: '',
        EndDate: '',
        EntryTypeID: '',
        Remarks: '',
    });

    const [employeeList, setEmployeeList] = useState([]);
    const [timeEntryTypeList, setTimeEntryTypeList] = useState([]);

    const { timeEntryId } = useParams();
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isDisable, setIsDisable] = useState(false);
    const navigate = useNavigate();
    const roles = JSON.parse(localStorage.getItem('Roles'));

    const userAccess = {
        hasView: roles?.includes('View') || false,
        hasAdmin: roles?.includes('Admin') || false,
        hasApprover: roles?.includes('Approver') || false,
        hasStandard: roles?.includes('Standard') || false,
    };

    const { hasView, hasAdmin, hasStandard, hasApprover } = userAccess;

    const handleInputChange = (e) => {
        const value = e.target.name === 'EntryTypeID' ? parseInt(e.target.value, 10) : e.target.value;
        setTimeEntryData({ ...timeEntryData, [e.target.name]: value });
    };

    // Function to calculate the duration in decimal format between DateEnd and DateStart
    const calculateDuration = () => {
        const startDate = new Date(timeEntryData.StartDate);
        const endDate = new Date(timeEntryData.EndDate);
        const durationInMinutes = (endDate - startDate) / (1000 * 60);

        // Convert duration to hours and minutes in decimal format
        const hours = Math.floor(durationInMinutes / 60);
        const minutes = durationInMinutes % 60;
        const durationInDecimal = hours + minutes / 60;

        return isNaN(durationInDecimal) ? 0 : durationInDecimal.toFixed(2);
    };

    useEffect(() => {
        const formattedDateTime = (dateTimeString) => {
            const dateObject = new Date(dateTimeString);
            const year = dateObject.getFullYear();
            const month = String(dateObject.getMonth() + 1).padStart(2, '0');
            const day = String(dateObject.getDate()).padStart(2, '0');
            const hours = String(dateObject.getHours()).padStart(2, '0');
            const minutes = String(dateObject.getMinutes()).padStart(2, '0');

            return `${year}-${month}-${day}T${hours}:${minutes}`;
        };

        const fetchData = async () => {
            if (timeEntryId) {
                setIsDisable(true);
                try {
                    const token = localStorage.getItem('token');
                    const response = await fetch(`${apiUrl}/TimeEntry/${timeEntryId}`, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`,
                        },
                    });

                    if (!response.ok) {
                        setErrorMessage('Failed to fetch time entry data');
                        setSuccessMessage('');
                        return;
                    }

                    const data = await response.json();
                    setTimeEntryData({
                        TransactionDate: formattedDateTime(data.Details.TransactionDate),
                        EmployeeID: data.Details.Employee.EmployeeID,
                        StartDate: formattedDateTime(data.Details.StartDate),
                        EndDate: formattedDateTime(data.Details.EndDate),
                        EntryTypeID: data.Details.EntryTypeID,
                        Remarks: data.Details.Remarks,
                    });
                    setIsDisable(false);
                } catch (error) {
                    setErrorMessage('Error fetching time entry data');
                    setSuccessMessage('');
                    setIsDisable(false);
                }
            }
        };

        const fetchEmployeeList = async () => {
            setIsDisable(true);
            const token = localStorage.getItem('token');
            try {
                const response = await fetch(`${apiUrl}/Employee/Namelist`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    setErrorMessage('Failed to fetch employee data');
                    setSuccessMessage('');
                    return;
                }

                const data = await response.json();
                setEmployeeList(data.Employee);
                setIsDisable(false);
            } catch (error) {
                setErrorMessage('Error fetching employee data');
                setSuccessMessage('');
                setIsDisable(false);
            }
        };

        const setTimeEntryTypes = () => {
            const timeEntryTypes = Object.keys(TimeEntryTypes).map(key => ({
                id: TimeEntryTypes[key],
                name: key,
            }));
            setTimeEntryTypeList(timeEntryTypes);
        };

        fetchData();
        fetchEmployeeList();
        setTimeEntryTypes();
    }, []);


    const handleForUpdate = async (e) => {
        e.preventDefault();
        const { EmployeeID, StartDate, EndDate, EntryTypeID, Remarks } = timeEntryData;

        const updateTimeEntry = {
            EmployeeID,
            StartDate,
            EndDate,
            EntryTypeID,
            Remarks,
        };

        const duration = calculateDuration(StartDate, EndDate);
        if (duration <= 0) {
            setErrorMessage('StartDate must be earlier than EndDate');
            setSuccessMessage('');
            return;
        }
        if (duration >= 24) {
            setErrorMessage('Duration must not exceed 24 hours..');
            setSuccessMessage('');
            return;
        }

        // Disable the button when the API process starts
        setIsDisable(true);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${apiUrl}/TimeEntry/Update/${timeEntryId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(updateTimeEntry),
            });

            if (response.ok) {
                const data = await response.json();
                setErrorMessage('');
                setSuccessMessage(data.Message);
                setIsDisable(false);
                navigate('/timeentry-list');
            }
        } catch (error) {
            setErrorMessage('Error updating time entry');
            setSuccessMessage('');
            setIsDisable(false);
        }
    };

    const {
        TransactionDate,
        EmployeeID,
        StartDate,
        EndDate,
        EntryTypeID,
        Remarks,
    } = timeEntryData;

    return (
        <div>
            {(userAccess.hasAdmin || userAccess.hasStandard) ? (
                <>
                    <h3>Update Time Entry</h3>
                    {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
                    {successMessage && <div className="alert alert-success">{successMessage}</div>}
                    <form onSubmit={handleForUpdate}>
                        <div className='mb-3'>
                            <label htmlFor='transactionDate' className='form-label'>Transaction Date</label>
                            <input
                                type="datetime-local"
                                className="form-control custom-width"
                                id="transactionDate"
                                name="TransactionDate"
                                value={TransactionDate}
                                readOnly={true}
                            />
                        </div>
                        <div className='mb-3'>
                            <label htmlFor='employeeID' className='form-label'>Employee</label>
                            <select
                                type="text"
                                className="form-control custom-width"
                                id="employeeID"
                                name="EmployeeID"
                                value={EmployeeID}
                                onChange={handleInputChange}
                                required
                            >
                                <option value="">---Select an employee---</option>
                                {employeeList.map(employee => (
                                    <option key={employee.EmployeeID} value={employee.EmployeeID}>
                                        {`${employee.Name}`}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className='mb-3'>
                            <label htmlFor='dateStart' className='form-label'>Time In</label>
                            <input
                                type="datetime-local"
                                className="form-control custom-width"
                                id="dateStart"
                                name="StartDate"
                                value={StartDate}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                        <div className="mb-3">
                            <label htmlFor="dateEnd" className="form-label">Time Out</label>
                            <input
                                type="datetime-local"
                                className="form-control custom-width"
                                id="dateEnd"
                                name="EndDate"
                                value={EndDate}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                        <div className='mb-3'>
                            <label htmlFor='duration' className='form-label'>Duration (hours)</label>
                            <input
                                type="text"
                                className="form-control custom-width"
                                id="duration"
                                name="duration"
                                value={calculateDuration()}
                                readOnly={true}
                            />
                        </div>
                        <div className='mb-3'>
                            <label htmlFor='timeEntryType' className='form-label'>Entry Type</label>
                            <select
                                type="text"
                                className="form-control custom-width"
                                id="timeEntryType"
                                name="EntryTypeID"
                                value={TimeEntryTypes[EntryTypeID]}
                                onChange={handleInputChange}
                                required
                            >
                                <option value="">---Select Entry Type---</option>
                                {timeEntryTypeList.map(timeEntryType => (
                                    <option key={timeEntryType.id} value={timeEntryType.id}>
                                        {timeEntryType.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="mb-3">
                            <label htmlFor="remarks" className="form-label">
                                Remarks
                            </label>
                            <input
                                type="text"
                                className="form-control custom-width"
                                id="remarks"
                                name="Remarks"
                                value={Remarks}
                                onChange={handleInputChange}
                                maxLength={255}
                                required
                            />
                        </div>
                        <Link to="/timeentry-list" className="btn btn-secondary me-2">Back</Link>
                        {(userAccess.hasStandard || userAccess.hasAdmin) && (
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={isDisable}
                            >
                                Update
                            </button>
                        )}
                    </form>
                </>
            ) : (
                <div>
                    <h3>No access!</h3>
                    <button className="btn btn-primary" onClick={() => navigate(-1)}>
                        Go Back
                    </button>
                </div>
            )}
        </div>
    );
};

export default TimeEntryUpdate;
